/**
 * Plain-language definitions and reference ranges for every performance metric
 * the result pages show.
 *
 * One file so a metric cannot be explained two different ways on two pages,
 * and so the caveats travel with the number. Several of these caveats are
 * specific to how this system computes things and materially change how a
 * value should be read:
 *
 *   - Sharpe and Sortino run on an equity curve with a point per trading day
 *     whether or not capital was deployed, so idle days drag them toward a
 *     measure of time in the market. Always read them next to Exposure.
 *   - CAGR is not extrapolated below a year; the tile relabels itself.
 *   - Backtests apply 0.25% slippage per side (entries, stop and time exits;
 *     not take-profits) and fill gapped-through stops at the open.
 *
 * `ranges` are reference bands, not pass marks. Tone drives the colour:
 *   bad | weak | ok | good | suspect
 */

export const METRIC_INFO = {
  total_return_pct: {
    label: 'Total Return',
    what: 'Percentage change in portfolio value from the starting funds to the end of the period. Not annualised and not risk-adjusted, so it says nothing about how much risk was taken to get there.',
    ranges: [
      { band: 'Below the index', verdict: 'An index fund would have done better for less effort', tone: 'bad' },
      { band: 'Above the index', verdict: 'Only meaningful once beta and drawdown are checked', tone: 'ok' },
    ],
    caveat: 'Compare against the Nifty 50 figure in the "vs Nifty 50" panel. Over a bull market almost any long-only strategy posts a large positive return.',
  },

  cagr_pct: {
    label: 'CAGR',
    what: 'Compound annual growth rate — the constant yearly rate that would turn the starting funds into the final value over the same span.',
    ranges: [
      { band: '< 0%', verdict: 'Losing money', tone: 'bad' },
      { band: '0 – 12%', verdict: 'Below the Nifty’s long-run average', tone: 'weak' },
      { band: '12 – 25%', verdict: 'Beating the index', tone: 'good' },
      { band: '25 – 40%', verdict: 'Strong — verify beta and exposure', tone: 'good' },
      { band: '> 40%', verdict: 'Rare over long samples; suspect overfitting or a short window', tone: 'suspect' },
    ],
    caveat: 'Spans under a year are NOT annualised — a 90-day result would otherwise compound +10% into ~+46%. The tile then reads "Period Return" instead.',
  },

  sharpe_ratio: {
    label: 'Sharpe Ratio',
    what: 'Return above the 6% risk-free rate per unit of total volatility, annualised. The standard measure of whether returns justified their bumpiness.',
    ranges: [
      { band: '< 0', verdict: 'Worse than holding cash', tone: 'bad' },
      { band: '0 – 0.5', verdict: 'Weak — returns barely compensate the risk', tone: 'weak' },
      { band: '0.5 – 1', verdict: 'Acceptable', tone: 'ok' },
      { band: '1 – 2', verdict: 'Good', tone: 'good' },
      { band: '2 – 3', verdict: 'Very good', tone: 'good' },
      { band: '> 3', verdict: 'Rare in live trading; on a backtest usually overfitting or too few trades', tone: 'suspect' },
    ],
    caveat: 'Computed on a curve that has a point per trading day whether or not capital was deployed. Idle days pull the average down faster than the volatility, so a low-exposure strategy is penalised. Read Exposure alongside, and the deployed-days figure where shown.',
  },

  active_sharpe_ratio: {
    label: 'Sharpe (deployed days)',
    what: 'The same Sharpe ratio computed only over days capital was actually at work, with idle days removed.',
    ranges: [
      { band: 'Close to headline Sharpe', verdict: 'The ratio reflects the trading itself', tone: 'good' },
      { band: 'Much higher than headline', verdict: 'The headline is being dragged down by idle days, not bad trades', tone: 'ok' },
    ],
    caveat: 'This is the undistorted number when exposure is low. It cannot be compared against published Sharpe ratios, which are computed on full calendar time.',
  },

  sortino_ratio: {
    label: 'Sortino Ratio',
    what: 'Like Sharpe, but it divides by downside volatility only. Upside swings are not counted as risk, which is closer to how losses actually hurt.',
    ranges: [
      { band: '< 0', verdict: 'Losing after the risk-free rate', tone: 'bad' },
      { band: '0 – 1', verdict: 'Weak', tone: 'weak' },
      { band: '1 – 2', verdict: 'Acceptable', tone: 'ok' },
      { band: '2 – 3', verdict: 'Good', tone: 'good' },
      { band: '> 3', verdict: 'Strong — same overfitting caution as Sharpe', tone: 'suspect' },
    ],
    caveat: 'Normally sits above Sharpe, often 1.3–2x. If Sortino is far above Sharpe the volatility is mostly upside; if the two are close, gains and losses are about equally choppy.',
  },

  max_drawdown_pct: {
    label: 'Max Drawdown',
    what: 'The worst peak-to-trough fall in portfolio value over the period. The single best proxy for whether a strategy is actually sittable through a bad stretch.',
    ranges: [
      { band: '< 10%', verdict: 'Shallow — check that exposure is not near zero', tone: 'good' },
      { band: '10 – 20%', verdict: 'Normal for a long-only equity system', tone: 'ok' },
      { band: '20 – 35%', verdict: 'Deep; needs real conviction to hold through', tone: 'weak' },
      { band: '> 35%', verdict: 'Most people abandon a system here, which turns a paper loss into a real one', tone: 'bad' },
    ],
    caveat: 'A backtest never tests whether you could stomach it. Judge against the drawdown duration, not just the depth.',
  },

  time_in_market_pct: {
    label: 'Exposure',
    what: 'Share of trading days on which at least one position was held. The companion to the "avg deployed" figure, which is how much of the capital was in play rather than how often.',
    ranges: [
      { band: '< 30%', verdict: 'Mostly in cash — Sharpe and Sortino above are largely measuring idleness', tone: 'suspect' },
      { band: '30 – 60%', verdict: 'Selective; capital spends a lot of time waiting', tone: 'ok' },
      { band: '60 – 90%', verdict: 'Typical for a long-only swing system', tone: 'good' },
      { band: '> 90%', verdict: 'Nearly always invested — close to buy-and-hold, so check beta', tone: 'ok' },
    ],
    caveat: 'Low exposure is not automatically bad: sitting out is a legitimate edge. But it makes every risk-adjusted ratio on this page harder to read, and it limits how much capital can compound.',
  },

  avg_exposure_pct: {
    label: 'Avg Deployed',
    what: 'Average share of the portfolio actually invested across all days — capital utilisation, as opposed to how often anything was held at all.',
    ranges: [
      { band: 'Far below Exposure', verdict: 'Positions are small relative to the book; sizing may be leaving capital idle', tone: 'ok' },
      { band: 'Close to Exposure', verdict: 'When holding, the book is close to fully invested', tone: 'good' },
    ],
    caveat: 'Persistently low utilisation often means max_positions or the sizing rule is the binding constraint, not the signal.',
  },

  expectancy_r: {
    label: 'Expectancy (R)',
    what: 'Average profit per trade expressed in units of the average loss. 0.2R means a typical trade nets a fifth of what a typical loser costs. This is what combines win rate with win/loss size into one number.',
    ranges: [
      { band: '≤ 0R', verdict: 'No edge — the strategy loses money per trade', tone: 'bad' },
      { band: '0 – 0.1R', verdict: 'Thin; costs and slippage can erase it entirely', tone: 'weak' },
      { band: '0.1 – 0.3R', verdict: 'Workable edge', tone: 'good' },
      { band: '0.3 – 0.5R', verdict: 'Strong', tone: 'good' },
      { band: '> 0.5R', verdict: 'Exceptional — check the trade count before believing it', tone: 'suspect' },
    ],
    caveat: 'Backtests here charge 0.25% slippage per side on entries and on stop and time exits, and fill stops gapped through at the open. Real fills on illiquid names can be worse. Needs 30+ trades before it means anything.',
  },

  win_rate_pct: {
    label: 'Win Rate',
    what: 'Share of closed trades that made money. On its own it says very little — a 30% win rate is excellent with large winners, and a 70% win rate loses money with large losers.',
    ranges: [
      { band: '< 40%', verdict: 'Fine only if winners are much bigger than losers', tone: 'ok' },
      { band: '40 – 60%', verdict: 'Typical for a trend/momentum system', tone: 'ok' },
      { band: '> 60%', verdict: 'High — verify that losers are not being cut late', tone: 'ok' },
    ],
    caveat: 'Always read with Expectancy and Profit Factor. Win rate alone cannot tell you whether a strategy makes money.',
  },

  profit_factor: {
    label: 'Profit Factor',
    what: 'Gross profit divided by gross loss. 1.0 means winners and losers exactly cancel before costs.',
    ranges: [
      { band: '< 1.0', verdict: 'Losing money', tone: 'bad' },
      { band: '1.0 – 1.25', verdict: 'Marginal; costs may consume it', tone: 'weak' },
      { band: '1.25 – 1.75', verdict: 'Healthy', tone: 'good' },
      { band: '> 2.0', verdict: 'Strong — rare on long samples, so check for overfitting', tone: 'suspect' },
    ],
    caveat: null,
  },

  beta: {
    label: 'Beta',
    what: 'How much the strategy moves for a 1% move in the Nifty 50. Beta 1 means it tracks the index; beta 2 means it amplifies it in both directions.',
    ranges: [
      { band: '< 0.5', verdict: 'Largely independent of the index — or simply under-exposed', tone: 'good' },
      { band: '0.5 – 1.0', verdict: 'Partly market-driven', tone: 'ok' },
      { band: '≈ 1.0', verdict: 'Moves with the market; returns are mostly market returns', tone: 'weak' },
      { band: '> 1.2', verdict: 'Amplifies the index — part of the return is leverage on direction, not selection', tone: 'suspect' },
    ],
    caveat: 'Headline beta is diluted by idle capital: a book deployed 40% of the time shows a low beta that reflects exposure, not defensiveness. The deployed-days beta beside it is the undiluted one.',
  },

  alpha_annual_pct: {
    label: 'Alpha (annualised)',
    what: 'Return left over after accounting for market exposure — the part beta cannot explain. This is the closest thing to a measure of skill on this page.',
    ranges: [
      { band: '|t| < 2', verdict: 'Not distinguishable from zero, whatever the size', tone: 'bad' },
      { band: '|t| ≥ 2, 0 – 5%', verdict: 'Real but modest edge', tone: 'ok' },
      { band: '|t| ≥ 2, 5 – 15%', verdict: 'Strong edge', tone: 'good' },
      { band: '|t| ≥ 2, > 15%', verdict: 'Verify the sample length and trade count', tone: 'suspect' },
    ],
    caveat: 'Read the t-statistic, not the alpha. A large alpha with |t| below 2 is consistent with chance — the common case over a single bull market. Also measured against a price index, so it is overstated by roughly the Nifty’s dividend yield (~1–1.5%/yr).',
  },

  information_ratio: {
    label: 'Information Ratio',
    what: 'Return above the benchmark per unit of tracking error — consistency of outperformance, rather than its size.',
    ranges: [
      { band: '< 0', verdict: 'Underperforms the index', tone: 'bad' },
      { band: '0 – 0.5', verdict: 'Inconsistent outperformance', tone: 'weak' },
      { band: '0.5 – 0.75', verdict: 'Good', tone: 'good' },
      { band: '> 1.0', verdict: 'Excellent — uncommon, so check the sample', tone: 'suspect' },
    ],
    caveat: null,
  },

  r_squared: {
    label: 'R²',
    what: 'Share of the strategy’s day-to-day variation explained by the index. 1.0 means it is effectively tracking the market.',
    ranges: [
      { band: '< 0.3', verdict: 'Largely independent of the index', tone: 'good' },
      { band: '0.3 – 0.7', verdict: 'Partly market-driven', tone: 'ok' },
      { band: '> 0.7', verdict: 'Mostly the market in disguise; alpha and beta are the real story', tone: 'weak' },
    ],
    caveat: null,
  },

  up_capture_pct: {
    label: 'Up Capture',
    what: 'Share of the index’s average gain captured on days the index rose. 120% means it gained 20% more than the index on up days.',
    ranges: [
      { band: '< 80%', verdict: 'Lags rallies', tone: 'weak' },
      { band: '80 – 120%', verdict: 'Participates roughly in line', tone: 'ok' },
      { band: '> 120%', verdict: 'Amplifies rallies — check down capture before calling it good', tone: 'ok' },
    ],
    caveat: 'Only useful next to Down Capture. The combination worth having is high up, low down.',
  },

  down_capture_pct: {
    label: 'Down Capture',
    what: 'Share of the index’s average loss taken on days the index fell. Lower is better; negative means it tended to rise when the index fell.',
    ranges: [
      { band: '< 50%', verdict: 'Strongly defensive', tone: 'good' },
      { band: '50 – 100%', verdict: 'Takes less than the full market fall', tone: 'ok' },
      { band: '> 100%', verdict: 'Falls harder than the index', tone: 'bad' },
    ],
    caveat: 'A low figure can simply mean the book was in cash during the falls — check Exposure.',
  },

  sharpe_ci: {
    label: 'Sharpe 95% CI',
    what: 'A bootstrap confidence interval for the Sharpe ratio, built by resampling blocks of the daily return series. It answers how much of the headline Sharpe is signal rather than luck of the sample.',
    ranges: [
      { band: 'Interval excludes 0', verdict: 'The Sharpe is distinguishable from no edge on this sample', tone: 'good' },
      { band: 'Interval includes 0', verdict: 'A flat return is inside the range of plausible outcomes', tone: 'bad' },
    ],
    caveat: 'Blocks rather than single days, because drawdowns are runs of bad days and resampling day-by-day would understate the spread. This tests the sample, not the strategy — it cannot detect overfitting.',
  },
};

export const TONE_CLASS = {
  bad:     'text-loss',
  weak:    'text-loss/80',
  ok:      'text-muted-foreground',
  good:    'text-win',
  suspect: 'text-amber-600',
};
